// src/pages/admin/UserListPage.tsx

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { useAdminStore } from '../../store/adminStore';
import { filterUsersBySearch, isSelfDelete } from '../../utils/adminUtils';
import type { ManagedUserWithAccess } from '../../models/adminTypes';

export function UserListPage() {
  const navigate = useNavigate();
  const session = useAppStore((s) => s.session);
  const users = useAdminStore((s) => s.users);
  const usersLoading = useAdminStore((s) => s.usersLoading);
  const usersError = useAdminStore((s) => s.usersError);
  const fetchUsers = useAdminStore((s) => s.fetchUsers);
  const deleteUser = useAdminStore((s) => s.deleteUser);
  const disableUser = useAdminStore((s) => s.disableUser);
  const enableUser = useAdminStore((s) => s.enableUser);

  const [searchText, setSearchText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<ManagedUserWithAccess | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const idToken = session?.idToken || '';

  // Get current user's sub from the token
  const currentUserSub = (() => {
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload.sub || '';
    } catch {
      return '';
    }
  })();

  useEffect(() => {
    if (idToken) {
      fetchUsers(idToken);
    }
  }, [idToken, fetchUsers]);

  const filteredUsers = filterUsersBySearch(users, searchText) as ManagedUserWithAccess[];

  const handleDelete = async (user: ManagedUserWithAccess) => {
    if (isSelfDelete(currentUserSub, user.userId)) {
      setActionError("You cannot delete your own account.");
      return;
    }
    setConfirmDelete(user);
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    try {
      setActionError(null);
      await deleteUser(idToken, confirmDelete.userId);
      setActionSuccess(`User ${confirmDelete.email} deleted.`);
      setConfirmDelete(null);
    } catch (error: unknown) {
      const message = (error as { message?: string })?.message || 'Failed to delete user';
      setActionError(message);
      setConfirmDelete(null);
    }
  };

  const handleDisable = async (user: ManagedUserWithAccess) => {
    try {
      setActionError(null);
      await disableUser(idToken, user.userId);
      setActionSuccess(`User ${user.email} disabled.`);
    } catch (error: unknown) {
      const message = (error as { message?: string })?.message || 'Failed to disable user';
      setActionError(message);
    }
  };

  const handleEnable = async (user: ManagedUserWithAccess) => {
    try {
      setActionError(null);
      await enableUser(idToken, user.userId);
      setActionSuccess(`User ${user.email} enabled.`);
    } catch (error: unknown) {
      const message = (error as { message?: string })?.message || 'Failed to enable user';
      setActionError(message);
    }
  };

  const handleResetPassword = async (user: ManagedUserWithAccess) => {
    try {
      setActionError(null);
      const { AdminService } = await import('../../services/adminService');
      const adminSvc = new AdminService();
      const result = await adminSvc.resetPassword(idToken, user.userId);
      if (result?.tempPassword) {
        setActionSuccess(
          `Password reset for ${user.email}.\nTemporary password: ${result.tempPassword}\nLogin: https://jawphotos.jamesandellen.com`
        );
      } else {
        setActionSuccess(`Password reset for ${user.email}.`);
      }
    } catch (error: unknown) {
      const message = (error as { message?: string })?.message || 'Failed to reset password';
      setActionError(message);
    }
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  if (usersLoading) {
    return (
      <div className="years-page" role="status" aria-label="Loading users">
        <div className="loading-indicator">Loading users…</div>
      </div>
    );
  }

  if (usersError) {
    return (
      <div className="years-page">
        <div className="error-state" role="alert">
          <p>{usersError}</p>
          <button
            className="retry-button"
            onClick={() => fetchUsers(idToken)}
            style={{ minHeight: '44px' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="years-page">
      <div className="years-header">
        <h1>👥 Users</h1>
        <button
          className="btn-primary"
          onClick={() => navigate('/admin/create')}
          style={{ minHeight: '44px' }}
        >
          + Create User
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <input
          className="input"
          type="search"
          placeholder="Search by email…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          aria-label="Search users by email"
        />
      </div>

      {/* Status messages */}
      {actionError && (
        <div className="error-state" role="alert" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
          <p>{actionError}</p>
        </div>
      )}
      {actionSuccess && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: 'var(--success-bg)',
          border: '1px solid var(--success)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--success)',
          fontWeight: 500,
        }} role="status">
          {actionSuccess}
        </div>
      )}

      {/* User list */}
      {filteredUsers.length === 0 ? (
        <p className="empty-state">No users found</p>
      ) : (
        <ul className="years-list" role="list">
          {filteredUsers.map((user) => (
            <li key={user.userId}>
              <div className="card" style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'default' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span className="card-title">{user.email}</span>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', alignItems: 'center' }}>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        padding: '0.15rem 0.5rem',
                        borderRadius: 'var(--radius-full)',
                        background: user.status === 'active' ? 'var(--success-bg)' : 'var(--warning-bg)',
                        color: user.status === 'active' ? 'var(--success)' : 'var(--warning)',
                      }}>
                        {user.status === 'active' ? '● Active' : '● Disabled'}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {user.accessSummary}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    className="refresh-button"
                    onClick={() => navigate(`/admin/users/${user.userId}/permissions`)}
                    style={{ minHeight: '36px', fontSize: '0.8rem' }}
                  >
                    📁 Permissions
                  </button>
                  {user.status === 'active' ? (
                    <button
                      className="refresh-button"
                      onClick={() => handleDisable(user)}
                      style={{ minHeight: '36px', fontSize: '0.8rem' }}
                    >
                      ⏸ Disable
                    </button>
                  ) : (
                    <button
                      className="refresh-button"
                      onClick={() => handleEnable(user)}
                      style={{ minHeight: '36px', fontSize: '0.8rem' }}
                    >
                      ▶ Enable
                    </button>
                  )}
                  <button
                    className="refresh-button"
                    onClick={() => handleResetPassword(user)}
                    style={{ minHeight: '36px', fontSize: '0.8rem' }}
                  >
                    🔑 Reset Password
                  </button>
                  <button
                    className="refresh-button"
                    onClick={() => handleDelete(user)}
                    style={{
                      minHeight: '36px',
                      fontSize: '0.8rem',
                      background: 'var(--error-bg)',
                      color: 'var(--error)',
                    }}
                  >
                    🗑 Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--overlay)',
            backdropFilter: 'blur(4px)',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-title"
        >
          <div style={{
            background: 'var(--surface)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-xl)',
            maxWidth: '400px',
            width: '90%',
            boxShadow: 'var(--shadow-lg)',
          }}>
            <h3 id="confirm-delete-title" style={{ marginBottom: '0.75rem' }}>
              Delete User?
            </h3>
            <p style={{ marginBottom: '1.25rem', color: 'var(--text-secondary)' }}>
              Are you sure you want to delete <strong>{confirmDelete.email}</strong>?
              This will also remove their access policy. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => setConfirmDelete(null)}
                style={{ minHeight: '44px' }}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={confirmDeleteAction}
                style={{
                  minHeight: '44px',
                  background: 'var(--error)',
                  boxShadow: 'none',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
